import { useCallback, useEffect, useRef, useState } from "react";
import api from "./api";

/**
 * Feb 2026 · Analog zu `useUnreadMessages`, aber für Kunden-Angebote.
 *
 * Der rote Badge auf der Übersichts-Kachel „Angebote" soll die Anzahl
 * neuer/ungelesener Angebote (`status="sent"`) anzeigen und verschwinden,
 * sobald der Kunde die Angebote-Seite geöffnet hat. Die Seite ruft dazu
 * beim Mount `POST /quotes/mark-all-viewed` auf und feuert danach das
 * Bump-Event, damit dieser Hook die aktuelle 0-Zahl sofort holt — kein
 * Warten auf den nächsten 30-Sekunden-Poll.
 */
const UNREAD_QUOTES_EVENT = "weissblom:unread-quotes-refresh";

export function bumpUnreadQuotes() {
  try {
    window.dispatchEvent(new CustomEvent(UNREAD_QUOTES_EVENT));
  } catch { /* older browsers — ignore */ }
}

export function useUnreadQuotes(role) {
  const [total, setTotal] = useState(0);
  const alive = useRef(true);

  const load = useCallback(async () => {
    // Nur Kunden/Family haben einen Angebots-Badge.
    if (role !== "customer" && role !== "family") return;
    try {
      const { data } = await api.get("/quotes/unread-count");
      if (!alive.current) return;
      setTotal(Number(data?.count || 0));
    } catch { /* silent — badge just won't update this cycle */ }
  }, [role]);

  useEffect(() => {
    alive.current = true;
    if (!role) return () => { alive.current = false; };
    load();
    const iv = setInterval(load, 30000);
    const onVisible = () => { if (document.visibilityState === "visible") load(); };
    const onBump = () => load();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener(UNREAD_QUOTES_EVENT, onBump);
    return () => {
      alive.current = false;
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener(UNREAD_QUOTES_EVENT, onBump);
    };
  }, [role, load]);

  return { total, refresh: load };
}
